import React, { useState, useEffect } from 'react';
import SubHeader from '../components/SubHeader/SubHeader';
import Footer from '../components/Footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faClock,
  faCalendarAlt,
  faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import '../styles/ShiftReport.css';
import { getShiftReport } from '../services/authService';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

const ShiftSkeleton = () => (
  <div className="sr-skeleton-item">
    <div className="sr-item-main">
      {/* Header skeleton */}
      <div className="sr-item-header">
        <div className="sr-item-title">
          <div className="skeleton-text skeleton-short"></div>
          <div className="skeleton-text skeleton-medium"></div>
        </div>
        <div className="skeleton-text skeleton-short"></div>
      </div>
      
      {/* Details skeleton */}
      <div className="sr-item-details">
        <div className="sr-item-info">
          <div className="sr-item-tickets">
            <div className="skeleton-text skeleton-short"></div>
            <div className="skeleton-text skeleton-short"></div>
          </div>
          <div className="sr-item-commission">
            <div className="skeleton-text skeleton-short"></div>
            <div className="skeleton-text skeleton-medium"></div>
          </div>
        </div>
        <div className="sr-item-times">
          <div className="sr-time">
            <div className="skeleton-text skeleton-medium"></div>
          </div>
          <div className="sr-time">
            <div className="skeleton-text skeleton-medium"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

function ShiftReport() {
  const [searchParams] = useSearchParams();
  const fromDate = searchParams.get('fromDate');
  const toDate = searchParams.get('toDate');
  
  // Xác định timeFilter từ fromDate và toDate
  const determineTimeFilter = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 'month';

    const from = new Date(fromDate);
    const to = new Date(toDate);
    const today = new Date();

    // Kiểm tra nếu là today
    if (from.toDateString() === to.toDateString() && 
        to.toDateString() === today.toDateString()) {
      return 'today';
    }

    // Kiểm tra nếu là month
    if (from.getDate() === 1 && 
        from.getMonth() === to.getMonth() &&
        to.toDateString() === today.toDateString()) {
      return 'month';
    }

    // Kiểm tra nếu là year
    if (from.getMonth() === 0 && from.getDate() === 1 &&
        to.toDateString() === today.toDateString()) {
      return 'year';
    }

    return 'month'; // Mặc định
  };

  const [timeFilter, setTimeFilter] = useState(() => 
    determineTimeFilter(fromDate, toDate)
  );

  useEffect(() => {
    setTimeFilter(determineTimeFilter(fromDate, toDate));
  }, [fromDate, toDate]);

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  useEffect(() => {
    fetchShifts();
  }, [timeFilter]);

  const getDateRange = () => {
    const today = new Date();
    const fromDate = new Date();
    
    switch(timeFilter) {
      case 'week':
        // Lấy ngày đầu tuần (thứ 2)
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        fromDate.setDate(diff);
        break;
      case 'month':
        fromDate.setDate(1);
        break;
      case 'year':
        fromDate.setMonth(0, 1);
        break;
      case 'custom':
        return {
          fromDate: customFromDate,
          toDate: customToDate
        };
      default: // today
        break;
    }

    return {
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  };

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const { fromDate, toDate } = getDateRange();
      const data = await getShiftReport(fromDate, toDate);
      setShifts(data.shifts || []);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách ca làm việc:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    if (value === 'custom') {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
    }
  };

  const handleDateChange = () => {
    if (customFromDate && customToDate) {
      fetchShifts();
    }
  };

  // Thêm hàm để format ngày
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Hàm để nhóm vé theo ngày
  const groupShiftsByDate = (shifts) => {
    const groups = {};
    
    shifts.forEach(shift => {
      const date = shift.date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(shift);
    });
    
    // Sắp xếp theo ngày mới nhất
    return Object.entries(groups)
      .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA));
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'WAITING': return 'Đang chờ';
      case 'CHECKIN': return 'Đang trong ca';
      case 'CHECKOUT': return 'Hoàn thành';
      default: return status;
    }
  };

  return (
    <div className="sr-container">
      <SubHeader title="Báo cáo ca làm việc" />
      
      <main className="sr-main">
        {/* Fixed section */}
        <div className="sr-fixed-section">
          {/* Dropdown filter */}
          <div className="sr-filter">
            <div className="sr-filter-select">
              <FontAwesomeIcon icon={faCalendarAlt} className="sr-filter-icon" />
              <select 
                value={timeFilter}
                onChange={(e) => handleTimeFilterChange(e.target.value)}
                className="sr-select"
              >
                <option value="month">Tháng này</option>
                <option value="week">Tuần này</option>
                <option value="year">Năm nay</option>
                <option value="custom">Tùy chọn thời gian</option>
              </select>
            </div>

            {showDatePicker && (
              <div className="sr-date-picker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update);
                    if (update[0] && update[1]) {
                      setCustomFromDate(update[0].toISOString().split('T')[0]);
                      setCustomToDate(update[1].toISOString().split('T')[0]);
                    }
                  }}
                  isClearable={true}
                  placeholderText="Từ ngày - Đến ngày"
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  dropdownMode="select"
                />
                <button 
                  className="sr-date-apply"
                  onClick={handleDateChange}
                  disabled={!startDate || !endDate}
                >
                  Áp dụng
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable list */}
        <div className="sr-list-container">
          <div className="sr-list">
            {loading ? (
              [...Array(10)].map((_, index) => (
                <ShiftSkeleton key={index} />
              ))
            ) : shifts.length === 0 ? (
              <div className="sr-empty-state">
                <FontAwesomeIcon icon={faClockRotateLeft} className="sr-empty-icon" />
                <p className="sr-empty-text">Không có dữ liệu ca làm việc</p>
              </div>
            ) : (
              groupShiftsByDate(shifts).map(([date, shifts]) => (
                <div key={date} className="sr-date-group">
                  <div className="sr-date-header">
                    <span>{date}</span>
                    <span className="sr-date-count">{shifts.length} ca</span>
                  </div>
                  {shifts.map(shift => (
                    <div key={shift.id} className={`sr-item ${shift.status.toLowerCase()}`}>
                      <div className="sr-item-main">
                        {/* Dòng 1: Mã và trạng thái */}
                        <div className="sr-item-header">
                          <div className="sr-item-title">
                            <span className="sr-item-code">#{shift.gate_shift_id}</span>
                            <span className="sr-item-gate">{shift.gate_name}</span>
                          </div>
                          <div className={`sr-item-status ${shift.status.toLowerCase()}`}>
                            <FontAwesomeIcon icon={shift.status === 'CHECKOUT' ? faCheckCircle : faClock} />
                            <span>{getStatusText(shift.status)}</span>
                          </div>
                        </div>
                        
                        {/* Dòng 2: Thông tin vé và thời gian */}
                        <div className="sr-item-details">
                          <div className="sr-item-info">
                              {/* <div className="sr-item-tickets">
                                <span className="sr-label">Số vé:</span>
                                <span className="sr-value">{shift.checked_tickets}</span>
                              </div> */}
                              <div className="sr-item-commission">
                                <span className="sr-label">Tiền đò:</span>
                                <span className="sr-value">
                                  {new Intl.NumberFormat('vi-VN', { 
                                    style: 'currency', 
                                    currency: 'VND',
                                    maximumFractionDigits: 0
                                  }).format(shift.total_commission)}
                                </span>
                              </div>
                          </div>
                          <div className="sr-item-times">
                            {shift.checkin_at && (
                              <div className="sr-time">
                                <span className="sr-label">Vào:</span>
                                <span className="sr-value">{shift.checkin_at}</span>
                              </div>
                            )}
                            {shift.checkout_at && (
                              <div className="sr-time">
                                <span className="sr-label">Ra:</span>
                                <span className="sr-value">{shift.checkout_at}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ShiftReport; 